import React from 'react';
import { Box, Paper, Typography, Button, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import EventBusyIcon from '@mui/icons-material/EventBusy';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import SettingsInputAntennaIcon from '@mui/icons-material/SettingsInputAntenna';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import BugReportIcon from '@mui/icons-material/BugReport';

import { sendUpdateScheduleStatus } from 'state/schedules';
import { useAppDispatch, useAppSelector } from 'hooks';

import { Link as RouterLink } from 'react-router-dom';

import { capitalize, round, toTimeDDMonYearFormat } from 'utils';
import {
    ANTENNA_KEYHOLE_WARNING_ELEVATION,
    ANTENNA_KEYHOLE_ERROR_ELEVATION,
} from 'config';
import { Schedule } from 'models/schedules';

import SatelliteSubSummary from './SatelliteSubSummary';
import AntennaSubSummary from './AntennaSubSummary';

import AzimuthSwatch from 'components/AzimuthSwatch';
import ElevationSwatch from 'components/ElevationSwatch';

interface Props {
    schedule: Schedule;
    onClick?: (schedule: Schedule) => void;
    /**
     * The context will indicate whether we bother displaying
     * the ground station name/details or the satellite name/details
     * generally because this will exist on a page
     */
    context: 'groundstation' | 'satellite';
}

const ScheduleDetail: React.FunctionComponent<Props> = ({
    schedule,
    context,
}) => {
    const theme = useTheme();

    const dispatch = useAppDispatch();
    const { scheduleStatusResponse } = useAppSelector((root) => root.schedules);

    const { orbit, antenna, pass_details: details, logs } = schedule;

    const { satellite } = orbit ?? {};

    // const { groundstation } = antenna;

    let button: React.ReactNode;

    const bookedStatus =
        schedule.status === 'booked' || schedule.status === 'auto_booked';
    const pendingStatus =
        schedule.status === 'to_confirm' || schedule.status === 'pending';

    const handleBookings = () => {
        let newStatus = schedule.status;

        if (pendingStatus) {
            newStatus = 'booked';
        }
        if (bookedStatus) {
            newStatus = 'pending';
        }

        dispatch(
            sendUpdateScheduleStatus({ id: schedule.id, status: newStatus }),
        );
    };

    if (bookedStatus) {
        button = (
            <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<EventBusyIcon />}
                onClick={handleBookings}
                disabled={
                    //stops the user from clicking on the button if loading, and disables button if communication has started (or on pending passes, has been ignored)
                    scheduleStatusResponse.status === 'loading' ||
                    schedule.start_time.getTime() <= new Date().getTime()
                }
            >
                Unbook
            </Button>
        );
    } else if (pendingStatus) {
        button = (
            <Button
                variant="outlined"
                color="success"
                size="small"
                startIcon={<EventAvailableIcon />}
                onClick={handleBookings}
                disabled={
                    scheduleStatusResponse.status === 'loading' ||
                    schedule.start_time.getTime() <= new Date().getTime()
                }
            >
                Book
            </Button>
        );
    }

    const subSummary =
        context === 'groundstation' ? (
            <SatelliteSubSummary schedule={schedule} satellite={satellite} />
        ) : (
            <AntennaSubSummary schedule={schedule} antenna={antenna} />
        );

    return (
        <Paper
            sx={{
                display: 'grid',
                gridTemplateRows: 'auto auto auto 1fr auto',
                background: theme.palette.background.details,
                px: 1,
                borderRadius: 1,
                rowGap: 1,
                breakInside: 'avoid',
            }}
        >
            {subSummary}

            {details ? (
                <Box
                    sx={{
                        display: 'grid',
                        rowGap: 1,
                        columnGap: 2,
                        gridTemplateColumns: 'auto 1fr',
                        [theme.breakpoints.up('lg')]: {
                            gridTemplateColumns: 'auto 1fr auto 1fr',
                        },
                        width: '100%',
                    }}
                >
                    <Box sx={{ fontWeight: 'bold' }}>Id:</Box>
                    <Box>{schedule.id}</Box>
                    <Box sx={{ fontWeight: 'bold' }}>Status:</Box>
                    <Box>{capitalize(schedule.status)}</Box>
                    <Box sx={{ fontWeight: 'bold' }}>Duration:</Box>
                    <Box>{round(schedule.duration)}s</Box>
                    <Box sx={{ fontWeight: 'bold' }}>Locked:</Box>
                    <Box>{schedule.locked ? 'True' : 'False'}</Box>
                    <Box sx={{ fontWeight: 'bold' }}>Start Time:</Box>
                    <Box>{toTimeDDMonYearFormat(schedule.start_time)}</Box>
                    <Box sx={{ fontWeight: 'bold' }}>Stop Time:</Box>
                    <Box>{toTimeDDMonYearFormat(schedule.stop_time)}</Box>
                    <Box sx={{ fontWeight: 'bold' }}>Start Azimuth:</Box>
                    <Box>
                        {round(schedule.start_azimuth)}°N{' '}
                        <AzimuthSwatch
                            rotation={schedule.start_azimuth}
                            margin="0px 4px"
                        />
                    </Box>
                    <Box sx={{ fontWeight: 'bold' }}>Final Azimuth:</Box>
                    <Box>
                        {round(schedule.final_azimuth)}°N
                        <AzimuthSwatch
                            rotation={schedule.final_azimuth}
                            margin="0px 4px"
                        />
                    </Box>
                    <Box sx={{ fontWeight: 'bold' }}>Minimum Elevation:</Box>
                    <Box>
                        {round(schedule.min_elevation, 2)}°
                        <ElevationSwatch
                            elevation={schedule.min_elevation}
                            margin="0px 4px"
                        />
                    </Box>
                    <Box sx={{ fontWeight: 'bold' }}>Maximum Elevation:</Box>
                    <Box>
                        {round(schedule.max_elevation, 2)}°
                        <ElevationSwatch
                            elevation={schedule.max_elevation}
                            margin="0px 4px"
                        />
                    </Box>

                    <Box sx={{ fontWeight: 'bold' }}>Closest Range:</Box>
                    <Box>{round(details.highest_slant_range)}km</Box>
                    <Box sx={{ fontWeight: 'bold' }}>Furthest Range:</Box>
                    <Box>
                        {round(
                            details.aos_slant_range > details.los_slant_range
                                ? details.aos_slant_range
                                : details.los_slant_range,
                        )}
                        km
                    </Box>

                    <Box sx={{ fontWeight: 'bold' }}>Sun Azimuth:</Box>
                    <Box>
                        {round(details.sun_azimuth)}°N
                        <AzimuthSwatch
                            rotation={details.sun_azimuth}
                            margin="0px 4px"
                        />
                    </Box>
                    <Box sx={{ fontWeight: 'bold' }}>Sun Elevation:</Box>
                    <Box>
                        {round(details.sun_elevation)}°
                        <ElevationSwatch
                            elevation={details.sun_elevation}
                            margin="0px 4px"
                        />
                    </Box>
                </Box>
            ) : (
                <Box
                    sx={{
                        display: 'grid',
                        width: '100%',
                    }}
                >
                    <Typography>Pass Details not Available</Typography>
                </Box>
            )}

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    pt: 1,
                    maxHeight: 260,
                    overflowY: 'auto',
                }}
            >
                {schedule.max_elevation > ANTENNA_KEYHOLE_WARNING_ELEVATION &&
                    schedule.max_elevation <
                        ANTENNA_KEYHOLE_ERROR_ELEVATION && (
                        <Paper
                            sx={{
                                display: 'grid',
                                gap: 1,
                                px: 1,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <WarningIcon fontSize="large" color="warning" />
                                <Typography variant="body1">
                                    <b>Warnings</b>
                                </Typography>
                            </Box>
                            <Typography variant="body1">
                                This Pass has a Maximum Elevation of °
                                {round(schedule.max_elevation, 2)} and will
                                experience Minor to Moderate Keyhole Effects.
                                Communication may be lost midway though the
                                pass.
                            </Typography>
                        </Paper>
                    )}
                {schedule.max_elevation > ANTENNA_KEYHOLE_ERROR_ELEVATION && (
                    <Paper
                        sx={{
                            display: 'grid',
                            gap: 1,
                            px: 1,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                            }}
                        >
                            <ErrorIcon fontSize="large" color="error" />
                            <Typography variant="body1">
                                <b>Warnings</b>
                            </Typography>
                        </Box>
                        <Typography variant="body1">
                            This Pass has a Maximum Elevation of{' '}
                            {round(schedule.max_elevation, 2)}° and will
                            experience Major Keyhole Effects. Communication will
                            likely be lost midway though the pass and for a
                            short period after the midway point.
                        </Typography>
                    </Paper>
                )}
                {logs
                    .slice()
                    .sort((a, b) => b.created.getTime() - a.created.getTime())
                    .map((log, idx) => (
                        <Paper
                            key={idx}
                            sx={{
                                display: 'grid',
                                gap: 1,
                                px: 1,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                }}
                            >
                                {log.level == 'debug' && (
                                    <BugReportIcon
                                        fontSize="large"
                                        color="success"
                                    />
                                )}
                                {log.level == 'info' && (
                                    <InfoIcon fontSize="large" color="info" />
                                )}
                                {log.level == 'warning' && (
                                    <WarningIcon
                                        fontSize="large"
                                        color="warning"
                                    />
                                )}
                                {log.level == 'error' && (
                                    <ErrorIcon fontSize="large" color="error" />
                                )}

                                <Typography variant="body1">
                                    <b>{toTimeDDMonYearFormat(log.created)}</b>
                                </Typography>
                            </Box>
                            <Typography variant="body1">{log.log}</Typography>
                        </Paper>
                    ))}
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingBottom: 1,
                }}
            >
                {/* Disabling this Button as the Page is goes to is incomplete */}
                {false && (
                    <IconButton
                        size="small"
                        color="primary"
                        component={RouterLink}
                        to={`/schedule/${schedule.id}`}
                    >
                        <SettingsInputAntennaIcon color="success" />
                    </IconButton>
                )}
                <Box />

                {button}
            </Box>
        </Paper>
    );
};

ScheduleDetail.displayName = 'ScheduleDetail';
export default ScheduleDetail;
